import { AlertCircle, X, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "../lib/motion";
import { useRef } from "react";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useI18n } from "../i18n/I18nProvider";

interface ErrorModalProps {
  error: string | null;
  onClose: () => void;
  onRetry?: () => void;
}

export default function ErrorModal({ error, onClose, onRetry }: ErrorModalProps) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  useEscapeKey(onClose, !!error);
  useFocusTrap(dialogRef, !!error);

  if (!error) return null;

  return (
    <AnimatePresence>
      <div
        className="bg-ink/40 fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="error-modal-title"
      >
        <motion.div
          ref={dialogRef}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="w-full max-w-md overflow-hidden rounded-[32px] border border-red-100 bg-white shadow-2xl"
        >
          <div className="p-8">
            <div className="mb-6 flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("error.dismiss")}
                className="rounded-full p-2 transition-colors hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:outline-none"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            <h3
              id="error-modal-title"
              className="text-ink serif mb-3 text-2xl font-light tracking-tight italic"
            >
              {t("error.title")}
            </h3>

            <p className="mb-8 text-sm leading-relaxed text-zinc-600">{error}</p>

            <div className="flex flex-col gap-3">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="bg-ink flex w-full items-center justify-center gap-2 rounded-full py-4 font-medium text-white transition-all hover:bg-emerald-600 focus-visible:ring-4 focus-visible:ring-emerald-500/30 focus-visible:outline-none active:scale-[0.98]"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" />
                  {t("error.retry")}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-full bg-zinc-100 py-4 font-medium text-zinc-600 transition-all hover:bg-zinc-200 focus-visible:ring-4 focus-visible:ring-zinc-400/40 focus-visible:outline-none active:scale-[0.98]"
              >
                {t("error.dismiss")}
              </button>
            </div>
          </div>

          <div className="border-t border-zinc-100 bg-zinc-50 px-8 py-4">
            <p className="text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
              {t("error.report")}
            </p>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
